/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). What the AI assistant can do
 * for one agreement, worked out from the agreement's own data: the notice
 * letter, a reminder, a price comparison, the invoice check and a re-bid. Each
 * job has a prompt, the steps it will take and a plan with the facts its report
 * gives (`contract-chat.ts` words them for the chat). Nothing here is sent
 * anywhere; the demo only prepares drafts.
 */
import type { ScriptedAnswer } from '../../ask/types';
import type { ScriptedStep } from '../../components/useScriptedSteps';
import { clausesForContract } from '../../data/clauses';
import type { Clause, ClauseKind } from '../../data/clauses';
import { PERSONA_ID, getCategory, getPerson } from '../../data/company';
import { getContract, noticeDeadline } from '../../data/contracts';
import { addDays, fmtDate, parseIsoDate, startOfToday } from '../../data/dates';
import { fmtMoneyCompact } from '../../data/format';
import {
  MATERIALS,
  MATERIAL_SUPPLIERS,
  TCO_BREAKDOWN,
  materialSuppliers,
} from '../../data/materials';
import { getSupplier, suppliersByCategory } from '../../data/suppliers';
import type {
  Contract,
  ContractId,
  MaterialId,
  SupplierId,
} from '../../data/types';
import { CONTRACT_COMPLIANCE } from './contracts-data';
import type { ContractCompliance } from './contracts-data';
import { LETTER_STEPS, buildLetter } from './letters';
import type { DraftLetter } from './letters';
import { buildRenewalEvents } from './renewals';
import type { RenewalEvent } from './renewals';

export type AgentActionKind =
  | 'letter'
  | 'reminder'
  | 'compare'
  | 'invoices'
  | 'rebid';

/** What a result card needs, one shape per kind of action. */
export interface LetterPlan {
  kind: 'letter';
  event: RenewalEvent;
  letter: DraftLetter;
}

export interface ReminderPlan {
  kind: 'reminder';
  /** The day the reminder fires. */
  date: Date;
  /** The date it reminds about, and a plain description of it. */
  targetDate: Date;
  targetLabel: string;
  leadDays: number;
  /** People who get it: the signed-in buyer, plus the owner when different. */
  recipients: string[];
  note: string | null;
}

interface CompareRow {
  supplierId: SupplierId;
  share: number;
  unitPrice: number;
  /** Price plus freight, duty, returns, downtime and rush orders where known. */
  allIn: number;
  isThis: boolean;
}

export interface ComparePlan {
  kind: 'compare';
  materialId: MaterialId;
  /** Price unit in words, e.g. "100 ft". */
  priceUnit: string;
  /** Unit of the price, e.g. "C". */
  unit: string;
  rows: CompareRow[];
  /** True when the all-in column includes quality and delay costs, not just freight. */
  fullCost: boolean;
  priceClause: Clause | null;
}

export interface InvoicesPlan {
  kind: 'invoices';
  compliance: ContractCompliance;
}

export interface RebidPlan {
  kind: 'rebid';
  title: string;
  baseline: string;
  yearlySpend: string;
  /** Short names of the suppliers the draft invites, incumbent first. */
  invitees: string[];
  materialId: MaterialId | null;
}

type AgentPlan =
  | LetterPlan
  | ReminderPlan
  | ComparePlan
  | InvoicesPlan
  | RebidPlan;

export interface AgentActionSpec {
  id: string;
  kind: AgentActionKind;
  prompt: string;
  aliases: string[];
  /** What the assistant will do, in order; the chat lists them before asking to go ahead. */
  steps: ScriptedStep[];
  plan: AgentPlan;
}

const REMINDER_NOTES: Partial<Record<ContractId, string>> = {
  'SA-CORV-2024-017':
    'The 90-day renewal decision date in §2.2 passed on Oct 2, 2026, so the reminder is set for the expiry.',
};

/** "Copper building wire, 1/0 gauge, 500 ft reels" becomes "copper building wire". */
export function materialNoun(name: string): string {
  const head = name.split(',')[0] ?? name;
  const isAcronym = /^[A-Z]{2}/.test(head);
  return isAcronym ? head : head.charAt(0).toLowerCase() + head.slice(1);
}

function letterPlanFor(contract: Contract): LetterPlan | null {
  if (contract.status === 'expired') return null;
  const events = buildRenewalEvents([contract], 100_000);
  const event =
    events.find((entry) => entry.kind === 'notice_deadline') ?? events[0];
  if (!event) return null;
  return {
    kind: 'letter',
    event,
    letter: buildLetter(contract.id, event.kind),
  };
}

const LETTER_NOUN: Record<string, string> = {
  'Notice of non-renewal': 'non-renewal notice',
  'Notice of statement of work close-out': 'close-out notice',
  'Renewal decision and proposed share of volume': 'renewal letter',
  'Request to extend or replace': 'extension request',
};

export function reminderPlanFor(contract: Contract): ReminderPlan | null {
  const today = startOfToday();
  const notice = noticeDeadline(contract);
  const end = parseIsoDate(contract.endDate);
  let targetDate: Date;
  let targetLabel: string;
  let leadDays: number;
  if (notice && notice.getTime() > today.getTime()) {
    targetDate = notice;
    targetLabel = 'the notice deadline';
    leadDays = 14;
  } else if (end.getTime() > today.getTime()) {
    targetDate = end;
    targetLabel = 'the agreement ends';
    leadDays = 30;
  } else {
    return null;
  }
  const earliest = addDays(today, 1);
  const wanted = addDays(targetDate, -leadDays);
  const owner = getPerson(contract.ownerId);
  const buyer = getPerson(PERSONA_ID);
  return {
    kind: 'reminder',
    date: wanted.getTime() < earliest.getTime() ? earliest : wanted,
    targetDate,
    targetLabel,
    leadDays,
    recipients: owner.id === buyer.id ? [buyer.name] : [buyer.name, owner.name],
    note: REMINDER_NOTES[contract.id] ?? null,
  };
}

function priceClauseOf(contractId: ContractId): Clause | null {
  const clauses = clausesForContract(contractId);
  return (
    clauses.find((clause) => clause.kind === 'price_adjustment') ??
    clauses.find((clause) => clause.kind === 'pricing') ??
    null
  );
}

function comparePlanFor(contract: Contract): ComparePlan | null {
  const own = MATERIAL_SUPPLIERS.find(
    (entry) => entry.contractId === contract.id,
  );
  if (!own) return null;
  const suppliers = materialSuppliers(own.materialId);
  if (suppliers.length < 2) return null;
  const material = MATERIALS[own.materialId];
  // Only the wire has a full cost build-up (returns, downtime, rush orders).
  const fullCost = own.materialId === 'MAT-THHN';
  return {
    kind: 'compare',
    materialId: own.materialId,
    unit: material.unit,
    priceUnit: material.unit === 'C' ? '100 ft' : material.unit,
    fullCost,
    priceClause: priceClauseOf(contract.id),
    rows: suppliers.map((entry) => ({
      supplierId: entry.supplierId,
      share: entry.share,
      unitPrice: entry.unitPrice,
      allIn: fullCost
        ? (TCO_BREAKDOWN.find((row) => row.supplierId === entry.supplierId)
            ?.tco ??
          entry.tco ??
          entry.landedPrice)
        : entry.landedPrice,
      isThis: entry.supplierId === contract.supplierId,
    })),
  };
}

function rebidPlanFor(
  contract: Contract,
  compare: ComparePlan | null,
): RebidPlan | null {
  if (contract.status === 'expired' || contract.annualValue <= 0) return null;
  const nextYear = parseIsoDate(contract.endDate).getFullYear() + 1;
  const years = `${nextYear} to ${nextYear + 2}`;
  const yearlySpend = `${fmtMoneyCompact(contract.annualValue)} a year`;
  if (compare) {
    const material = MATERIALS[compare.materialId];
    const own = compare.rows.find((row) => row.isThis);
    const head = material.name.split(',').slice(0, 2).join(',');
    return {
      kind: 'rebid',
      title: `${head}, ${years}`,
      baseline: `${own ? `$${own.unitPrice.toFixed(2)}` : 'Current price'} per ${compare.priceUnit} today`,
      yearlySpend: `${material.annualVolumeLabel} a year across suppliers, ${yearlySpend} with ${getSupplier(contract.supplierId).shortName}`,
      invitees: compare.rows
        .slice()
        .sort((a, b) => Number(b.isThis) - Number(a.isThis))
        .map((row) => getSupplier(row.supplierId).shortName),
      materialId: compare.materialId,
    };
  }
  const supplier = getSupplier(contract.supplierId);
  const others = suppliersByCategory(contract.categoryId)
    .filter((entry) => entry.id !== supplier.id)
    .slice(0, 3);
  return {
    kind: 'rebid',
    title: `${getCategory(contract.categoryId).name}, ${years}`,
    baseline: `${yearlySpend} today`,
    yearlySpend: `${yearlySpend} with ${supplier.shortName}`,
    invitees: [supplier.shortName, ...others.map((entry) => entry.shortName)],
    materialId: null,
  };
}

function compareStepsFor(
  contract: Contract,
  plan: ComparePlan,
): ScriptedStep[] {
  const others = plan.rows.filter((row) => !row.isThis).length;
  return [
    {
      label: plan.priceClause
        ? `Reading §${plan.priceClause.section}: ${plan.priceClause.label}`
        : 'Reading the price clause',
      durationMs: 700,
    },
    {
      label: `Pulling prices from the ${others} other ${others === 1 ? 'supplier' : 'suppliers'} of ${materialNoun(MATERIALS[plan.materialId].name)}`,
      durationMs: 800,
    },
    {
      label: `Putting every price on the same basis, per ${plan.priceUnit} delivered`,
      detail: `Checked against ${getSupplier(contract.supplierId).shortName}'s current price`,
      durationMs: 800,
    },
  ];
}

/** Everything the agent can do on this agreement, in the order it is offered. */
export function agentActionSpecsFor(contractId: ContractId): AgentActionSpec[] {
  const contract = getContract(contractId);
  const supplier = getSupplier(contract.supplierId);
  const specs: AgentActionSpec[] = [];

  const letter = letterPlanFor(contract);
  if (letter) {
    const noun = LETTER_NOUN[letter.letter.heading] ?? 'letter';
    specs.push({
      id: 'letter',
      kind: 'letter',
      prompt: `Draft the ${noun}`,
      aliases: [
        'Draft the renewal notice',
        'Draft the non-renewal notice',
        'Draft the notice letter',
        'Write the renewal letter',
      ],
      steps: LETTER_STEPS,
      plan: letter,
    });
  }

  const reminder = reminderPlanFor(contract);
  if (reminder) {
    const atNotice = reminder.targetLabel === 'the notice deadline';
    specs.push({
      id: 'reminder',
      kind: 'reminder',
      prompt: atNotice
        ? 'Set a reminder before the notice deadline'
        : 'Set a reminder before the agreement ends',
      aliases: [
        'Set a reminder before the notice date',
        'Remind me before the notice deadline',
        'Remind me before the notice date',
        'Remind me before the agreement ends',
        'Set a reminder',
        'Remind me before it expires',
      ],
      steps: [
        { label: 'Reading the term and notice clauses', durationMs: 600 },
        {
          label: `Finding ${atNotice ? 'the notice deadline' : 'the end date'}`,
          detail: fmtDate(reminder.targetDate),
          durationMs: 600,
        },
        {
          label: 'Adding the reminder to the calendar',
          detail: `${reminder.leadDays} days before`,
          durationMs: 700,
        },
      ],
      plan: reminder,
    });
  }

  const compare = comparePlanFor(contract);
  if (compare) {
    const noun = materialNoun(MATERIALS[compare.materialId].name);
    specs.push({
      id: 'compare',
      kind: 'compare',
      prompt: `Compare this price with the other ${noun} suppliers`,
      aliases: [
        `Compare the price clause with other ${noun} suppliers`,
        'Compare this price with other suppliers',
        'How does this price compare?',
        'Is this a good price?',
      ],
      steps: compareStepsFor(contract, compare),
      plan: compare,
    });
  }

  const compliance = CONTRACT_COMPLIANCE[contractId];
  if (compliance.invoicesChecked > 0) {
    specs.push({
      id: 'invoices',
      kind: 'invoices',
      prompt: "Check this year's invoices against the agreement",
      aliases: [
        'Check the last 12 months of invoices',
        'Check recent invoices against this agreement',
        `Has ${supplier.shortName} overcharged us?`,
        'Have we been overcharged?',
      ],
      steps: [
        {
          label: `Finding this year's ${supplier.shortName} invoices`,
          detail: `${compliance.invoicesChecked} invoices`,
          durationMs: 700,
        },
        {
          label: 'Checking each one against the enforced clauses',
          durationMs: 900,
        },
        {
          label: 'Adding up what was billed above the agreement',
          durationMs: 700,
        },
      ],
      plan: { kind: 'invoices', compliance },
    });
  }

  const rebid = rebidPlanFor(contract, compare);
  if (rebid) {
    const noun = compare
      ? materialNoun(MATERIALS[compare.materialId].name)
      : null;
    specs.push({
      id: 'rebid',
      kind: 'rebid',
      prompt: noun
        ? `Start a re-bid for ${noun}`
        : 'Start a re-bid for this agreement',
      aliases: [
        'Start a re-bid',
        'Re-bid this agreement',
        'Put this agreement out to bid',
        'Start competitive bids',
      ],
      steps: [
        {
          label: 'Reading the term, price and volume in this agreement',
          durationMs: 700,
        },
        { label: 'Finding approved and qualifying suppliers', durationMs: 800 },
        {
          label: 'Drafting the bid request with today’s price as the baseline',
          durationMs: 800,
        },
      ],
      plan: rebid,
    });
  }

  return specs;
}

type StarterPick = { question: string } | { action: AgentActionKind };

const STARTER_PICKS: Partial<Record<ContractId, StarterPick[]>> = {
  'MSA-GEN-2025-001': [
    { question: 'What service levels does Gently AI commit to?' },
    { question: 'How much notice do we need to give to not renew?' },
    { action: 'letter' },
    { action: 'reminder' },
  ],
  'SA-CORV-2024-017': [
    {
      question: 'When does the agreement expire and what do we need to decide?',
    },
    { action: 'letter' },
    { action: 'compare' },
    { question: 'How does the volume rebate work?' },
  ],
};

/** The suggested question whose main citation is a clause of one of these kinds. */
function questionAbout(
  contractId: ContractId,
  kinds: ClauseKind[],
  questions: ScriptedAnswer[],
): string | undefined {
  const sections = clausesForContract(contractId)
    .filter((clause) => kinds.includes(clause.kind))
    .map((clause) => clause.section);
  return questions.find((entry) => {
    const main = entry.citations[0];
    return main !== undefined && sections.includes(main.ref);
  })?.question;
}

/**
 * Three or four suggestions for the empty state, mixing questions and jobs.
 * The two agreements in the demo story have a hand-picked list; the rest are
 * built from the agreement's renewal, price and rebate clauses.
 */
export function agentStarters(
  contractId: ContractId,
  questions: ScriptedAnswer[],
  specs: AgentActionSpec[],
): string[] {
  const promptOf = (kind: AgentActionKind) =>
    specs.find((spec) => spec.kind === kind)?.prompt;
  const picks = STARTER_PICKS[contractId];
  const resolved = picks
    ? picks.map((pick) =>
        'question' in pick
          ? questions.find((entry) => entry.question === pick.question)
              ?.question
          : promptOf(pick.action),
      )
    : [
        questionAbout(contractId, ['auto_renewal', 'termination'], questions) ??
          questions[0]?.question,
        promptOf('letter'),
        promptOf('compare') ?? promptOf('reminder'),
        questionAbout(contractId, ['rebate'], questions) ??
          promptOf('invoices') ??
          questions[1]?.question,
      ];
  return [
    ...new Set(resolved.filter((text): text is string => text !== undefined)),
  ].slice(0, 4);
}
